import { useIsPeopleUnlockButtonLoading } from '@aibees/elephant/components/people-unlock/hooks/use-is-people-unlock-button-loading';
import { doesAiAnalysisHasAnyUpdates } from '@aibees/elephant/components/person-ai-analysis-info/utils';
import { QUICK_INFO_BUTTONS } from '@aibees/elephant/components/quick-info-group/components/quick-info-button';
import { useActionButtonContext } from '@aibees/elephant/context/action-button-context';
import { useUnlockPeoplePhoneNumber } from '@aibees/elephant/hooks/people';
import { ENRICHMENT_STATE } from '@aibees/elephant/types';
import { ANALYSIS_STATUS } from '@aibees/elephant/types/personality-analysis';
import { keyframes } from '@mui/material';
import { PeopleEmailStatus, PeopleTableInfo } from '../../types';
import {
  contactDetailsUnlockableItems,
  doesContactDetailHaveAnyUpdates,
  isJobChangeHappened,
} from '../people-drawer/components/contact-details/utils';
import { useHandleUnlockEmails } from '../people-email-popover/hooks/use-handle-unlock-emails';
import {
  getEmailQucikInfoStatus,
  getEmailsCleared,
  isAnyEmailNeedsReverification,
} from '../people-email-popover/utils';
import { badgeMap, QuickInfoBadgeInfo } from './types';

const rotateAnimation = keyframes`
from {
  transform: rotate(0deg);
}
to {
  transform: rotate(360deg);
}`;

export function doesEmailEnrichmentHaveAnyUpdates(data?: PeopleTableInfo) {
  const hasExpiredEmail = hasAnyExpiredEmail(data);
  return (
    (!!data?.changes?.EMAIL?.length || hasExpiredEmail) &&
    data?.revelation?.email?.state !== ENRICHMENT_STATE.PROCESSING
  );
}

export function hasAnyExpiredEmail(data?: PeopleTableInfo) {
  const clearedEmails = getEmailsCleared(data?.revelation?.email);

  let hasExpiredEmail = false;

  for (const email of clearedEmails) {
    if (email.status === PeopleEmailStatus.EXPIRED) hasExpiredEmail = true;
  }

  return hasExpiredEmail;
}

// TODO @mehdi-aibees please check all the eslint-disable-next-line react-hooks/rules-of-hooks errors and fix them

export const emailEnrichmentBadgeMap: (
  data: PeopleTableInfo
) => QuickInfoBadgeInfo = (data: PeopleTableInfo) => {
  const hasUpdate = doesEmailEnrichmentHaveAnyUpdates(data);
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { loading } = useHandleUnlockEmails(data);
  const needsEnrichEmail = isJobChangeHappened(data);

  if (loading) {
    return {
      icon: 'baseline-autorenew',
      color: 'primary',
      iconSx: { animation: `${rotateAnimation} 1s linear infinite` },
      variant: 'filled',
    };
  }

  if (hasUpdate || needsEnrichEmail) {
    return {
      icon: 'notifications-active',
      color: 'primary',
    };
  }

  const needsReverify = isAnyEmailNeedsReverification(data.revelation?.email);
  if (needsReverify) {
    return {
      text: 'B+',
      color: 'success',
    };
  }

  return badgeMap[
    getEmailQucikInfoStatus(getEmailsCleared(data.revelation?.email))
  ];
};

export const phoneNumberEnrichmentBadgeMap: (
  data: PeopleTableInfo
) => QuickInfoBadgeInfo = (data: PeopleTableInfo) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { loading, unlocked } = useUnlockPeoplePhoneNumber(data);

  if (loading) {
    return {
      icon: 'baseline-autorenew',
      color: 'primary',
      iconSx: { animation: `${rotateAnimation} 1s linear infinite` },
      variant: 'filled',
    };
  }

  if (!unlocked) {
    return {
      icon: 'questionMark',
      color: 'default',
    };
  }

  if (unlocked) {
    return {
      text: 'A+',
      color: 'success',
      variant: 'filled',
    };
  }

  // Default fallback
  return {
    icon: 'questionMark',
    color: 'default',
  };
};

export const contactDetailsBadgeMap: (
  data: PeopleTableInfo
) => QuickInfoBadgeInfo = (data: PeopleTableInfo) => {
  const state = data.revelation?.contact?.state ?? 'default';
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { loadingContactDetails } = useIsPeopleUnlockButtonLoading(data);
  const hasUpdate = doesContactDetailHaveAnyUpdates(data);

  if (loadingContactDetails)
    return {
      icon: 'baseline-autorenew',
      color: 'primary',
      iconSx: { animation: `${rotateAnimation} 1s linear infinite` },
      variant: 'filled',
    };

  if (hasUpdate) {
    return {
      icon: 'notifications-active',
      color: 'primary',
    };
  }

  switch (state) {
    case 'default':
      return {
        icon: 'questionMark',
        color: 'default',
      };
    case ENRICHMENT_STATE.PROCESSING:
      return {
        icon: 'baseline-autorenew',
        color: 'primary',
        iconSx: { animation: `${rotateAnimation} 1s linear infinite` },
        variant: 'filled',
      };
    case ENRICHMENT_STATE.FAILED:
      return {
        icon: 'info-circle-outline',
        color: 'error',
      };
    case ENRICHMENT_STATE.SUCCESS:
      return {
        text: 'A+',
        color: 'success',
        variant: 'filled',
      };
    default:
      return {
        icon: 'questionMark',
        color: 'default',
      };
  }
};

export const aiAnalysisBadgeMap: (
  data: PeopleTableInfo
) => QuickInfoBadgeInfo = (data: PeopleTableInfo) => {
  const hasUpdate = doesAiAnalysisHasAnyUpdates(data);
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { personalityLoading } = useIsPeopleUnlockButtonLoading(data);
  if (personalityLoading) {
    return {
      icon: 'baseline-autorenew',
      color: 'primary',
      iconSx: { animation: `${rotateAnimation} 1s linear infinite` },
      variant: 'filled',
    };
  }

  if (hasUpdate) {
    return {
      icon: 'notifications-active',
      color: 'primary',
    };
  }

  return badgeMap[
    data.revelation?.analysis?.state === ENRICHMENT_STATE.FAILED
      ? ANALYSIS_STATUS.ANALYSIS_FAILED
      : data.revelation?.analysis?.content?.status ?? 'default'
  ];
};

/**
 * The rule every badge shares: while the backend reports the revelation as
 * PROCESSING, or the record is mid-action in our own context, the loading badge
 * outranks every other state — including hiding the badge. An in-flight run must
 * never blank out, and each of these surfaces has a state that would otherwise
 * hide it (a Not Found email stays FAILED until the server picks the retry up; a
 * phone reveal hides on FAILED; contact details hide once nothing is unlockable).
 *
 * Same two signals the matching `use*Loading` hooks derive their spinner from,
 * minus their `useSaveRecord().isLoading` term — that is a per-observer flag on
 * a mutation these read-only callers never fire, so it is always false here.
 */
function useIsEnrichmentInProgress(
  data: PeopleTableInfo,
  type: QUICK_INFO_BUTTONS,
  state?: ENRICHMENT_STATE
) {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { isItemProcessing } = useActionButtonContext();

  return (
    state === ENRICHMENT_STATE.PROCESSING ||
    isItemProcessing({ id: data?.id ?? '', type })
  );
}

export const emailEnrichmentHideBadge: (data: PeopleTableInfo) => boolean = (
  data
) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const inProgress = useIsEnrichmentInProgress(
    data,
    QUICK_INFO_BUTTONS.EMAIL_ENRICHMENT,
    data.revelation?.email?.state
  );
  if (inProgress) return false;

  const isFailed = data.revelation?.email?.state === ENRICHMENT_STATE.FAILED;
  const clearedEmails = getEmailsCleared(data?.revelation?.email);
  const statuses = clearedEmails?.map((i) => i.status);
  const hasUpdate = doesEmailEnrichmentHaveAnyUpdates(data);
  const needsEnrichEmail = isJobChangeHappened(data);

  if (hasUpdate || needsEnrichEmail) return false;

  return (
    (isFailed && !statuses?.includes(PeopleEmailStatus.INVALID)) ||
    (!!data?.revelation?.email &&
      data.revelation?.email.state !== ENRICHMENT_STATE.PROCESSING &&
      !clearedEmails.length)
  );
};

export const phoneNumberEnrichmentHideBadge: (
  data: PeopleTableInfo
) => boolean = (data) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const inProgress = useIsEnrichmentInProgress(
    data,
    QUICK_INFO_BUTTONS.PHONENUMBER_ENRICHMENT,
    data.revelation?.phone_cached?.state
  );
  if (inProgress) return false;

  const isFailed =
    data.revelation?.phone_cached?.state === ENRICHMENT_STATE.FAILED;
  return isFailed;
};

export const contactDetailsHideBadge: (data: PeopleTableInfo) => boolean = (
  data
) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const inProgress = useIsEnrichmentInProgress(
    data,
    QUICK_INFO_BUTTONS.CONTACT_ENRICHMENT,
    data.revelation?.contact?.state
  );
  if (inProgress) return false;

  const unlockableItems = contactDetailsUnlockableItems(data);
  const hasUpdate = doesContactDetailHaveAnyUpdates(data);
  // TODO: this third term is probably dead. A company change always sets
  // CONTACT ∋ EXPERIENCE too, so changes.CONTACT is non-empty and `hasUpdate`
  // is already true whenever this is. Kept because "probably dead" is not
  // "provably dead" — it only holds for as long as that backend invariant does.
  const hasJobChange = isJobChangeHappened(data);

  return !unlockableItems.length && !hasUpdate && !hasJobChange;
};
